import {
  DOCUMENT_CLASSIFICATION_FILTER_ALL,
  documentMatchesClassificationFilter,
  type DocumentClassificationFilterId,
} from "@/components/case-checking/documentClassificationFilters";
import { sortDocuments, type DocumentSortMode, type SortableDocument } from "@/components/case-checking/documentListSort";
import type { DocumentAdditionQueue, DocumentAdditionQueueItem } from "@/services/api/case-checking";

export type ReviewDocumentRow = SortableDocument & {
  id?: string;
  classification: string;
  fileType?: string;
  valid?: boolean;
  classificationError?: string | null;
};

function parseSortTime(iso: string | null | undefined): number {
  if (!iso?.trim()) return Number.NaN;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.NaN : t;
}

function queueItemMatchesSearch(item: DocumentAdditionQueueItem, query: string): boolean {
  if (!query) return true;
  return item.label.toLowerCase().includes(query);
}

function documentMatchesSearch(doc: ReviewDocumentRow, query: string): boolean {
  if (!query) return true;
  return (
    doc.name.toLowerCase().includes(query) ||
    doc.classification.toLowerCase().includes(query)
  );
}

export function queuedUploadDocumentIds(queue: DocumentAdditionQueue | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const item of queue?.items ?? []) {
    if (item.kind === "upload" && item.document_id) {
      ids.add(item.document_id);
    }
  }
  return ids;
}

export function sortQueueItemsByAddedDesc(items: DocumentAdditionQueueItem[]): DocumentAdditionQueueItem[] {
  return [...items].sort((a, b) => {
    const ta = parseSortTime(a.added_at);
    const tb = parseSortTime(b.added_at);
    if (Number.isNaN(ta) && Number.isNaN(tb)) return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return tb - ta;
  });
}

export type DocumentListSections = {
  queuedRows: DocumentAdditionQueueItem[];
  reviewRows: ReviewDocumentRow[];
};

export function buildDocumentListSections({
  queue,
  documents,
  search,
  classificationFilter,
  sortMode,
}: {
  queue: DocumentAdditionQueue | null | undefined;
  documents: ReviewDocumentRow[];
  search: string;
  classificationFilter: DocumentClassificationFilterId;
  sortMode: DocumentSortMode;
}): DocumentListSections {
  const q = search.trim().toLowerCase();
  const stagedIds = queuedUploadDocumentIds(queue);

  const queuedRows = sortQueueItemsByAddedDesc(
    (queue?.items ?? []).filter((item) => queueItemMatchesSearch(item, q)),
  );

  const filtered = documents.filter((doc) => {
    if (doc.id && stagedIds.has(doc.id)) return false;
    if (!documentMatchesSearch(doc, q)) return false;
    if (classificationFilter !== DOCUMENT_CLASSIFICATION_FILTER_ALL) {
      if (!documentMatchesClassificationFilter(doc, classificationFilter)) return false;
    }
    return true;
  });

  const reviewRows = sortDocuments(filtered, sortMode) as ReviewDocumentRow[];

  return {
    queuedRows,
    reviewRows,
  };
}

export function formatQueueAddedLabel(addedAt: string): string {
  const t = parseSortTime(addedAt);
  if (Number.isNaN(t)) return "Added";
  return `Added ${new Date(t).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
}
